import { ensureUiScope } from '../../services/api';
import type {
  ProofReadinessAction,
  ProofReadinessIssue,
  ProofReadinessModel,
  ProofReadinessSummary,
  ProofReadinessVerdict,
} from '../../services/reportsApi';
import type { Device } from '../../services/devicesApi';
import { resolveArtifactFlowPreset } from '../../utils/reportFlow';
import { buildScopedIncidentsPath } from '../../utils/incidentsNavigation';
import { humanizeAreaLabel, humanizeZoneLabel } from '../../components/proof/proofDisplayUtils';
import { resolveConfiguredCameraLabel } from '../../domain/customerLabels/customerFacingControlLabel';
import type { RoutedActionResult, RoutedCta } from '../../components/proof/proofActionRouting';

function humanizeUnderscoreLabel(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (match) => match.toUpperCase());
}

function shortId(value: string): string {
  const text = String(value || '').trim();
  if (!text) return '';
  const parts = text.split('_');
  return parts[0] || text;
}

export function lookupLabeledValue(
  lookup: { get?: (key: string) => string | undefined } | Record<string, string> | null | undefined,
  raw: string,
): string {
  const key = String(raw || '').trim();
  if (!key) return '';
  const viaMap = String((lookup as any)?.get?.(key) || '').trim();
  if (viaMap) return viaMap;
  const viaObject = String((lookup as any)?.[key] || '').trim();
  return viaObject || key;
}

export function trimmedString(value: unknown): string | undefined {
  const text = String(value || '').trim();
  return text || undefined;
}

export function readPath(source: any, path: string): unknown {
  if (!source || typeof source !== 'object') return undefined;
  return path.split('.').reduce((current: any, segment) => {
    if (current == null) return undefined;
    if (/^\d+$/.test(segment)) {
      const index = Number(segment);
      return Array.isArray(current) ? current[index] : undefined;
    }
    return current[segment];
  }, source);
}

export function firstStringFromPaths(source: any, paths: string[]): string | undefined {
  for (const path of paths) {
    const value = trimmedString(readPath(source, path));
    if (value) return value;
  }
  return undefined;
}

export function firstDefinedString(...values: unknown[]): string | undefined {
  for (const value of values) {
    const text = trimmedString(value);
    if (text) return text;
  }
  return undefined;
}

export function reviewOutcomeDisplayLabel(value: unknown): string {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return 'Unknown';
  switch (raw) {
    case 'true_event':
      return 'Confirmed event';
    case 'false_positive':
      return 'False positive';
    case 'expected_activity':
      return 'Expected activity';
    case 'insufficient_evidence':
      return 'Insufficient evidence';
    case 'demo_or_test_activity':
      return 'Special-case activity';
    case 'duplicate':
      return 'Duplicate';
    case 'unknown':
      return 'Not reviewed yet';
    default:
      return humanizeUnderscoreLabel(raw);
  }
}

export function ownershipStateDisplayLabel(value: unknown): string {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return 'Unknown';
  switch (raw) {
    case 'unassigned':
      return 'Needs owner';
    case 'assigned':
      return 'Owned and active';
    case 'resolved':
      return 'Resolved';
    default:
      return humanizeUnderscoreLabel(raw);
  }
}

export function correctiveActionStateDisplayLabel(value: unknown): string {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return 'Unknown';
  switch (raw) {
    case 'not_started':
      return 'No work order yet';
    case 'linked':
      return 'Linked';
    case 'assigned':
      return 'Assigned';
    case 'in_progress':
      return 'In progress';
    case 'resolved':
      return 'Resolved';
    default:
      return humanizeUnderscoreLabel(raw);
  }
}

export function measurementStateDisplayLabel(value: unknown): string {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return 'Unknown';
  switch (raw) {
    case 'needs_review':
      return 'Needs review first';
    case 'not_applicable':
      return 'Not applicable';
    case 'action_pending':
      return 'Action still open';
    case 'ready_for_followup':
      return 'Ready for impact measurement';
    case 'measured':
      return 'Measured';
    default:
      return humanizeUnderscoreLabel(raw);
  }
}

export function actionSnapshotStateDisplayLabel(value: unknown): string {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return 'Unknown';
  switch (raw) {
    case 'no_action':
      return 'No action captured';
    case 'in_progress':
      return 'Action in progress';
    case 'followup_scheduled':
      return 'Action scheduled';
    case 'closed':
      return 'Closed';
    default:
      return humanizeUnderscoreLabel(raw);
  }
}

export function escalationStateDisplayLabel(value: unknown): string {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return 'Unknown';
  switch (raw) {
    case 'clear':
      return 'No active escalation';
    case 'scheduled':
      return 'Action scheduled';
    case 'escalated':
      return 'Escalation active';
    case 'overdue':
      return 'Needs escalation attention';
    default:
      return humanizeUnderscoreLabel(raw);
  }
}

export function formatCount(value: unknown): string | undefined {
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? numeric.toLocaleString() : undefined;
}

export function isInternalPolicyIdentifier(value?: string | null): boolean {
  const text = String(value || '').trim();
  if (!text || text.includes(' ')) return false;
  if (/^demo\d+pol$/i.test(text)) return true;
  if (/^forklift_person$/i.test(text)) return true;
  if (/^near_miss$/i.test(text)) return true;
  if (/^zone_violation$/i.test(text)) return true;
  return /^[a-z][a-z0-9_]*$/i.test(text) && text.length <= 40;
}

export function customerFacingSafetyRuleLabel(raw?: string | null): string {
  const text = String(raw || '').trim();
  if (!text) return 'Safety event';
  if (/^incident$/i.test(text) || /incident activity/i.test(text)) return 'Safety event';
  if (
    /near[_ -]?miss/i.test(text)
    || /policy_forklift_proximity/i.test(text)
    || /pedestrian in forklift area/i.test(text)
    || /pedestrian activity in forklift/i.test(text)
  ) {
    return 'Pedestrian Activity in Forklift Zone';
  }
  if (/zone_violation|zone violation|danger-zone entry/i.test(text)) return 'Pedestrian Activity in Forklift Zone';
  if (/^demo\d+pol$/i.test(text)) return 'Pedestrian Activity in Forklift Zone';
  return text;
}

export function resolveCustomerFacingPatternLabel(args: {
  preferredLabel?: string | null;
  policyTypeId?: string | null;
  incidents?: Array<Record<string, unknown>>;
  policyTypeOptions?: Array<{ value: string; label: string }>;
}): string {
  const readIncidentPolicyLabel = (incident: Record<string, unknown>): string => {
    const label = firstStringFromPaths(incident, [
      'canonical_labels.policy_label',
      'safety_case.labels.policy_label',
      'policy_label',
      'policy_name',
      'title',
      'canonical_labels.incident_label',
      'safety_case.labels.incident_label',
    ]);
    if (label && !isInternalPolicyIdentifier(label)) {
      return customerFacingSafetyRuleLabel(label);
    }
    return '';
  };

  for (const incident of args.incidents || []) {
    const label = readIncidentPolicyLabel(incident);
    if (label) return label;
  }

  const policyId = String(args.policyTypeId || '').trim();
  const option = (args.policyTypeOptions || []).find((entry) => entry.value === policyId);
  if (option?.label) {
    return customerFacingSafetyRuleLabel(option.label);
  }

  const preferred = String(args.preferredLabel || '').trim();
  if (preferred) {
    return customerFacingSafetyRuleLabel(preferred);
  }

  return customerFacingSafetyRuleLabel(policyId || 'Safety risk');
}

export function customerFacingAreaLabel(raw?: string | null): string {
  const text = String(raw || '').trim();
  if (!text) return '';
  if (/^unavailable$|^not captured yet$/i.test(text)) return '';
  if (/^demo5tes(\s+area)?$/i.test(text)) return 'Restricted Forklift Zone';
  if (/newfork.*pipel/i.test(text)) return 'Forklift operating area';
  return humanizeAreaLabel(text) || text;
}

export function customerFacingCameraLabel(raw?: string | null): string {
  const text = String(raw || '').trim();
  if (!text) return '';
  const configured = resolveConfiguredCameraLabel(text);
  if (configured) return configured;
  if (/^demo5cameras$/i.test(text)) return 'Forklift Area Cameras';
  return text;
}

export function formatPercent(value: unknown): string | null {
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) return null;
  const rounded = Math.round(numeric * 10) / 10;
  return rounded.toLocaleString(undefined, {
    minimumFractionDigits: Number.isInteger(rounded) ? 0 : 1,
    maximumFractionDigits: 1,
  });
}

export function buildWindowRiskPatternSummary(
  incidents: any[],
): NonNullable<ProofReadinessSummary['risk_pattern_summary']> | null {
  if (!Array.isArray(incidents) || incidents.length <= 0) return null;

  const zoneCounts = new Map<string, number>();
  const patternCounts = new Map<string, number>();
  let totalIncidents = 0;

  incidents.forEach((incident) => {
    if (!incident || typeof incident !== 'object') return;
    totalIncidents += 1;

    const zoneId = String(firstStringFromPaths(incident, ['zone_id', 'zoneId']) || '').trim();
    const rawZoneLabel = firstDefinedString(
      firstStringFromPaths(incident, [
        'canonical_labels.zone_label',
        'safety_case.labels.zone_label',
      ]),
      firstStringFromPaths(incident, ['zone_name', 'zoneName']),
      humanizeZoneLabel(zoneId),
      zoneId,
    );
    const zoneLabel = customerFacingAreaLabel(rawZoneLabel || zoneId)
      || humanizeZoneLabel(rawZoneLabel || zoneId)
      || rawZoneLabel
      || zoneId
      || 'Selected operating area';
    zoneCounts.set(zoneLabel, (zoneCounts.get(zoneLabel) || 0) + 1);

    const rawPatternLabel = firstDefinedString(
      firstStringFromPaths(incident, [
        'canonical_labels.policy_label',
        'safety_case.labels.policy_label',
      ]),
      firstStringFromPaths(incident, ['policy_name', 'triggered_by.policy_name']),
      firstStringFromPaths(incident, [
        'canonical_labels.incident_type_label',
        'canonical_labels.incident_type_key',
        'safety_case.scope.policy_type',
      ]),
      firstStringFromPaths(incident, ['incident_type', 'incidentType']),
      firstStringFromPaths(incident, ['title', 'incident_title']),
    );
    const patternLabel = customerFacingSafetyRuleLabel(rawPatternLabel || 'Safety event');
    patternCounts.set(patternLabel, (patternCounts.get(patternLabel) || 0) + 1);
  });

  if (totalIncidents <= 0) return null;

  const [topHotspot, topHotspotIncidentCount = 0] = Array.from(zoneCounts.entries())
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0] || [];
  const [strongestPatternLabel, strongestPatternCount = 0] = Array.from(patternCounts.entries())
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0] || [];
  const concentrationPercent = topHotspotIncidentCount > 0
    ? Math.round((topHotspotIncidentCount / totalIncidents) * 1000) / 10
    : null;
  const repeatPatternCount = Array.from(patternCounts.values()).filter((count) => count >= 2).length;

  let state: NonNullable<ProofReadinessSummary['risk_pattern_summary']>['state'] = 'isolated';
  let label = 'Isolated event';
  if (
    topHotspotIncidentCount >= 6
    || (concentrationPercent != null && concentrationPercent >= 40)
    || strongestPatternCount >= 3
  ) {
    state = 'recurring_hotspot';
    label = 'Recurring hotspot';
  } else if (
    topHotspotIncidentCount >= 3
    || (concentrationPercent != null && concentrationPercent >= 25)
    || repeatPatternCount > 0
  ) {
    state = 'emerging_pattern';
    label = 'Emerging recurring risk';
  }

  const hotspotCountLabel = formatCount(topHotspotIncidentCount) || String(topHotspotIncidentCount);
  const concentrationLabel = formatPercent(concentrationPercent);
  const summary = topHotspot
    ? concentrationLabel
      ? `${topHotspot} accounts for ${hotspotCountLabel} incident${topHotspotIncidentCount === 1 ? '' : 's'} (${concentrationLabel}% of this reporting period).`
      : `${topHotspot} is the main repeated area in this reporting period.`
    : strongestPatternLabel
      ? `${strongestPatternLabel} is the main recurring risk in this reporting period.`
      : 'This reporting period does not yet show a durable recurring risk.';

  return {
    state,
    label,
    top_hotspot: topHotspot || null,
    top_hotspot_incident_count: topHotspotIncidentCount || null,
    concentration_percent: concentrationPercent,
    repeat_pattern_count: repeatPatternCount,
    strongest_pattern_label: strongestPatternLabel || null,
    strongest_pattern_count: strongestPatternCount || null,
    summary,
  };
}

export function incidentGroupingKey(source: any): string {
  const incidentType = String(firstStringFromPaths(source, ['incident_type', 'incidentType']) || '').trim();
  const zoneId = String(firstStringFromPaths(source, ['zone_id', 'zoneId']) || '').trim();
  const cameraId = String(firstStringFromPaths(source, ['camera_id', 'cameraId']) || '').trim();
  const deviceId = String(firstStringFromPaths(source, ['device_id', 'deviceId']) || '').trim();
  return [incidentType, zoneId, cameraId, deviceId].join('|');
}

export function proofSurfaceFromPreset(searchParams: URLSearchParams): 'corrective' | 'package' | 'reliability' {
  const explicitSurface = String(searchParams.get('surface') || '').trim().toLowerCase();
  if (explicitSurface === 'corrective') return 'corrective';
  if (explicitSurface === 'package' || explicitSurface === 'evidence') return 'package';
  if (explicitSurface === 'reliability' || explicitSurface === 'trust') return 'reliability';
  const scopedCaseId = String(searchParams.get('case_id') || searchParams.get('caseId') || '').trim();
  const scopedPackageId = String(searchParams.get('proof_package_id') || searchParams.get('proofPackageId') || '').trim();
  const refreshEvidencePack = String(searchParams.get('refresh_evidence_pack') || '').trim().toLowerCase();
  if (scopedCaseId || scopedPackageId || refreshEvidencePack === '1' || refreshEvidencePack === 'true') {
    return 'package';
  }
  const preset = resolveArtifactFlowPreset(searchParams);
  if (!preset) return 'package';
  if (preset.task === 'investigate_incident' || preset.kind === 'proof_pack') return 'package';
  if (preset.task === 'technical_reliability' || preset.kind === 'reliability_bundle') return 'reliability';
  return 'package';
}

export function _scopedSiteId(): string {
  try {
    const scope = ensureUiScope();
    return String(scope.siteId || '').trim();
  } catch {
    return '';
  }
}

export function _scopedOrgId(): string {
  try {
    const scope = ensureUiScope();
    return String(scope.orgId || '').trim();
  } catch {
    return '';
  }
}

export function _titleCase(value: string): string {
  const text = String(value || '').trim();
  if (!text) return '';
  return text
    .replace(/_/g, ' ')
    .split(/\s+/)
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1).toLowerCase() : word))
    .join(' ');
}

export function _isSelectableDeviceForIncidentsFilter(device: Device | null | undefined): boolean {
  if (!device) return false;
  const deviceId = String((device as any).device_id || '').trim();
  if (!deviceId) return false;
  const base = (deviceId.split('_')[0] || deviceId).trim().toLowerCase();
  if (base.startsWith('cam-') || base.startsWith('cam_') || base.startsWith('camera-') || base.startsWith('camera_')) {
    return false;
  }
  const deviceType = String((device as any).device_type || (device as any).type || '').trim().toLowerCase();
  if (deviceType === 'camera') return false;
  return true;
}

export function _deviceFilterLabel(device: Device): string {
  const deviceIdRaw = String((device as any).device_id || '').trim();
  if (!deviceIdRaw) return '';
  const base = shortId(deviceIdRaw);
  const match = /^warehouse-(\d+)$/i.exec(base);
  if (match && match[1]) return `Warehouse Unit ${match[1]}`;
  const name = String((device as any).name || '').trim();
  const metadata: any = (device as any)?.device_metadata || {};
  const ip = String((device as any).ip_address || metadata?.ip_address || metadata?.health_api_host || '').trim();
  const core = name || base || deviceIdRaw;
  return ip ? core : `${core} (no IP)`;
}

export function actionPriorityRank(priority?: string): number {
  const normalized = String(priority || '').toLowerCase();
  if (normalized === 'high') return 0;
  if (normalized === 'medium') return 1;
  if (normalized === 'low') return 2;
  return 3;
}

export function hasFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

export function pluralize(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

export function formatNumber(count: number): string {
  return new Intl.NumberFormat('en-US').format(count);
}

export function normalizedIssueLink(issue?: ProofReadinessIssue): string | null {
  const links = issue?.links || {};
  for (const key of Object.keys(links)) {
    const href = links[key];
    if (typeof href === 'string' && href) return href;
  }
  return null;
}

export function evidenceMetricsAvailable(readiness?: ProofReadinessModel | null): boolean {
  const evidenceOperatorStatus = String(readiness?.summary?.evidence_operator_status || '').toLowerCase();
  if (evidenceOperatorStatus && evidenceOperatorStatus !== 'unavailable') return true;
  const evidence = readiness?.evidence;
  if (!evidence) return false;
  return [
    evidence.complete_count,
    evidence.partial_count,
    evidence.failed_count,
    evidence.missing_high_risk_clips,
    evidence.delayed_capture_count,
    evidence.clip_coverage_rate,
    evidence.snapshot_coverage_rate,
  ].some((value) => hasFiniteNumber(value));
}

export function reliabilityMetricsAvailable(readiness?: ProofReadinessModel | null): boolean {
  const reliabilityOperatorStatus = String(readiness?.summary?.reliability_operator_status || '').toLowerCase();
  if (reliabilityOperatorStatus && reliabilityOperatorStatus !== 'unavailable') return true;
  const reliability = readiness?.reliability;
  const reliabilityStatus = String(readiness?.summary?.reliability_status || '').trim();
  if (reliabilityStatus) return true;
  if (!reliability) return false;
  return [
    reliability.event_to_evidence_p95_sec,
    reliability.capture_failure_rate,
    reliability.processing_failure_rate,
    reliability.upload_delay_p95_sec,
    reliability.cameras_online_rate,
    reliability.devices_online_rate,
  ].some((value) => hasFiniteNumber(value));
}

export function cameraAvailabilityAvailable(readiness?: ProofReadinessModel | null): boolean {
  return hasFiniteNumber(readiness?.summary?.cameras_online)
    || hasFiniteNumber(readiness?.summary?.cameras_total)
    || hasFiniteNumber(readiness?.reliability?.cameras_online_rate);
}

export function firstIncidentHref(readiness?: ProofReadinessModel | null, proofPackPreviewData?: any): string | null {
  const incidentsCurated = readiness?.incidents_curated;
  const curated = Array.isArray(incidentsCurated) ? incidentsCurated : [];
  for (const incident of curated) {
    const links = incident?.links || {};
    for (const key of ['incident', 'diagnostics', 'live_view', 'device_control']) {
      const href = links?.[key];
      if (typeof href === 'string' && href) return href;
    }
  }
  const topIssues = readiness?.top_issues;
  const issues = Array.isArray(topIssues) ? topIssues : [];
  for (const issue of issues) {
    const href = normalizedIssueLink(issue);
    if (href) return href;
  }
  const topActions = proofPackPreviewData?.case_file?.top_actions;
  const caseActions = Array.isArray(topActions) ? topActions : [];
  for (const action of caseActions) {
    const links = action?.links || {};
    for (const key of ['incident', 'diagnostics', 'live_view', 'device_control']) {
      const href = links?.[key];
      if (typeof href === 'string' && href) return href;
    }
  }
  return null;
}

export function supportingVisuals(visuals?: any[]): any[] {
  return Array.isArray(visuals)
    ? visuals.filter((visual) => !['case_status_scorecard', 'top_case_actions', 'incident_timeline'].includes(String(visual?.id || '')))
    : [];
}

export function sanitizeVerdictFactors(
  verdict: ProofReadinessVerdict | undefined,
  opts: { evidenceAvailable: boolean; reliabilityAvailable: boolean; cameraAvailabilityAvailable: boolean },
): string[] {
  const factors = verdict?.factors;
  const rawFactors = Array.isArray(factors) ? factors : [];
  return rawFactors.filter((factor) => {
    const text = String(factor || '').toLowerCase();
    if (!opts.evidenceAvailable && /(clip|snapshot|evidence coverage|capture failure|evidence arrives)/.test(text)) return false;
    if (!opts.reliabilityAvailable && /(reliability|upload delay|event to evidence|failure rate)/.test(text)) return false;
    if (!opts.cameraAvailabilityAvailable && /(camera availability|offline or unstable cameras)/.test(text)) return false;
    return true;
  });
}

export function buildVerdictReason(
  verdict: ProofReadinessVerdict | undefined,
  readiness: ProofReadinessModel | null | undefined,
  proofPackPreviewData: any,
  opts: { evidenceAvailable: boolean; reliabilityAvailable: boolean },
): string {
  const status = String(verdict?.status || '').toUpperCase();
  const backendReason = String(verdict?.reason || '').trim();
  const openTotalRaw = proofPackPreviewData?.case_file?.summary?.open_total;
  const openTotal = hasFiniteNumber(openTotalRaw) ? Number(openTotalRaw) : null;
  const highCriticalRaw = proofPackPreviewData?.case_file?.summary?.high_or_critical_total;
  const highRisk = hasFiniteNumber(readiness?.summary?.high_risk_incidents)
    ? Number(readiness?.summary?.high_risk_incidents)
    : hasFiniteNumber(highCriticalRaw)
      ? Number(highCriticalRaw)
      : null;
  const unavailableLabels = [
    opts.evidenceAvailable ? null : 'evidence metrics',
    opts.reliabilityAvailable ? null : 'reliability metrics',
  ].filter(Boolean) as string[];

  const shouldRewriteOpenMetricsReason = openTotal != null && openTotal > 0 && unavailableLabels.length > 0;
  if (openTotal != null && openTotal > 0 && highRisk === 0 && unavailableLabels.length === 2) {
    return `No high-risk evidence records were found, but ${formatNumber(openTotal)} evidence records remain open and evidence/reliability metrics are missing from this report window.`;
  }
  if (
    backendReason
    && !shouldRewriteOpenMetricsReason
    && (
      status === 'NOT_READY'
      || status === 'NEEDS_REVIEW'
      || /have not been reviewed|pipeline degraded|metrics unavailable/i.test(backendReason)
    )
  ) {
    return backendReason;
  }

  const parts: string[] = [];
  if (openTotal != null && openTotal > 0) {
    parts.push(`${formatNumber(openTotal)} evidence record${openTotal === 1 ? '' : 's'} remain open.`);
  } else if (openTotal === 0) {
    parts.push('No evidence records remain open.');
  }

  if (openTotal != null && openTotal > 0 && unavailableLabels.length) {
    const metricsLabel = unavailableLabels.length === 2
      ? 'Evidence and reliability metrics are missing from this review window.'
      : `${unavailableLabels[0].charAt(0).toUpperCase()}${unavailableLabels[0].slice(1)} are not included in this report window.`;
    return `${parts[0]} ${metricsLabel}`.trim();
  }

  if (highRisk === 0) {
    parts.push('No high-risk evidence records found.');
  }

  if (highRisk === 0 && unavailableLabels.length) {
    const metricsLabel = unavailableLabels.length === 2
      ? 'evidence/reliability metrics are missing'
      : `${unavailableLabels[0]} are not included`;
    parts.push(`${metricsLabel.charAt(0).toUpperCase()}${metricsLabel.slice(1)} for this report window.`);
  } else if (highRisk != null && highRisk > 0 && unavailableLabels.length) {
    const metricsLabel = unavailableLabels.length === 2
      ? 'Evidence and reliability metrics are missing'
      : `${unavailableLabels[0].charAt(0).toUpperCase()}${unavailableLabels[0].slice(1)} are not included`;
    parts.push(`${formatNumber(highRisk)} high-risk event record${highRisk === 1 ? '' : 's'} were found. ${metricsLabel} for this report window.`);
  } else if (unavailableLabels.length) {
    const metricsLabel = unavailableLabels.length === 2
      ? 'Evidence and reliability metrics are missing'
      : `${unavailableLabels[0].charAt(0).toUpperCase()}${unavailableLabels[0].slice(1)} are not included`;
    parts.push(`${metricsLabel} for this report window.`);
  }

  return parts.join(' ') || verdict?.reason || 'Readiness could not be fully explained from the available data.';
}

export function pipelineDegraded(verdict?: ProofReadinessVerdict): boolean {
  const reason = String(verdict?.reason || '').toLowerCase();
  const rawFactors = verdict?.factors;
  const factors = Array.isArray(rawFactors) ? rawFactors : [];
  return reason.includes('pipeline degraded')
    || factors.some((factor) => String(factor || '').toLowerCase().includes('pipeline degraded'));
}

export function isZoneViolationIssue(issue?: ProofReadinessIssue): boolean {
  const text = [
    issue?.id,
    issue?.title,
    issue?.kind,
  ].map((value) => String(value || '').toLowerCase()).join(' ');
  return text.includes('zone_violation') || text.includes('zone violation');
}

export function firstIssueHref(issues?: ProofReadinessIssue[]): string | null {
  const issueList = Array.isArray(issues) ? issues : [];
  for (const issue of issueList) {
    const href = normalizedIssueLink(issue);
    if (href) return href;
  }
  return null;
}

export function dedupeActions(actions: ProofReadinessAction[]): ProofReadinessAction[] {
  const seen = new Set<string>();
  return actions.filter((action) => {
    const key = String(action.id || action.label || '').trim().toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function joinWithAnd(clauses: string[]): string {
  if (!clauses.length) return '';
  if (clauses.length === 1) return clauses[0];
  if (clauses.length === 2) return `${clauses[0]} and ${clauses[1]}`;
  return `${clauses.slice(0, -1).join(', ')}, and ${clauses[clauses.length - 1]}`;
}

export function clipCaptureDisabled(
  readiness?: ProofReadinessModel | null,
  verdict?: ProofReadinessVerdict | null,
): boolean {
  const issues = readiness?.top_issues || [];
  if (issues.some((issue) => String(issue?.id || '') === 'evidence:clip_capture_disabled')) return true;
  const actions = readiness?.actions || [];
  if (actions.some((action) => String(action?.id || '') === 'enable_clip_capture_required_types')) return true;
  const rawFactors = verdict?.factors;
  const factors = Array.isArray(rawFactors) ? rawFactors : [];
  return factors.some((factor) => /clip capture is disabled|required clip capture|required clips disabled for some incident types/i.test(String(factor)));
}

export function inferenceTrustDegraded(
  readiness?: ProofReadinessModel | null,
  verdict?: ProofReadinessVerdict,
): boolean {
  if ((readiness?.actions || []).some((action) => String(action?.id || '') === 'fix_degraded_inference')) return true;
  const status = String(readiness?.summary?.reliability_status || '').toLowerCase();
  if (status.includes('detection pipeline')) return true;
  if (status.includes('inference') && (status.includes('degrad') || status.includes('degraded'))) return true;
  return pipelineDegraded(verdict);
}

export function buildOperatorBlockingVerdictSentence(params: {
  status: string;
  openIncidents: number | null;
  clipDisabled: boolean;
  inferenceDegraded: boolean;
}): string | null {
  const { status, openIncidents, clipDisabled, inferenceDegraded } = params;
  if (status !== 'NOT_READY' && status !== 'NEEDS_REVIEW') return null;
  const clauses: string[] = [];
  if (openIncidents != null && openIncidents > 0) {
    clauses.push(`${formatNumber(openIncidents)} event record${openIncidents === 1 ? '' : 's'} remain open`);
  }
  if (clipDisabled) clauses.push('required clip capture is disabled for some incident types');
  if (inferenceDegraded) clauses.push('the edge detection pipeline had health issues during this reporting period');
  if (!clauses.length) return null;
  const prefix = status === 'NEEDS_REVIEW'
    ? 'Operator follow-up is still needed because '
    : 'A firm conclusion is not yet supported because ';
  return `${prefix}${joinWithAnd(clauses)}.`;
}

export function buildOperatorVerdictFactors(params: {
  openIncidents: number | null;
  highRiskIncidents: number | null | undefined;
  clipDisabled: boolean;
  inferenceDegraded: boolean;
}): string[] {
  const { openIncidents, highRiskIncidents, clipDisabled, inferenceDegraded } = params;
  const out: string[] = [];
  if (highRiskIncidents === 0) {
    out.push('No high-risk evidence records found');
  } else if (typeof highRiskIncidents === 'number' && highRiskIncidents > 0) {
    out.push(`${formatNumber(highRiskIncidents)} high-risk evidence records`);
  }
  if (openIncidents != null && openIncidents > 0) {
    out.push(`${formatNumber(openIncidents)} open evidence records`);
  }
  if (clipDisabled) out.push('Required clips disabled for some incident types');
  if (inferenceDegraded) out.push('Detection pipeline health issue');
  return out.slice(0, 4);
}

const PROOF_ACTION_DISPLAY_ORDER = [
  'enable_clip_capture_required_types',
  'restore_high_risk_clips',
  'fix_degraded_inference',
  'investigate_degraded_pipeline',
  'reduce_evidence_delay',
  'review_open_zone_violations',
  'review_open_incidents',
  'review_hotspot',
  'fix_offline_hardware',
  'review_high_risk',
  'regenerate_proof_readiness_inputs',
  'generate_proof_with_evidence_metrics',
  'confirm_reliability_metrics',
  'refresh_validation',
  'review_warnings_before_approval',
];

export function proofActionDisplayRank(action?: ProofReadinessAction): number {
  const id = String(action?.id || '');
  const idx = PROOF_ACTION_DISPLAY_ORDER.indexOf(id);
  if (idx >= 0) return idx;
  return 500;
}

export function sortProofActionsForDisplay(actions: ProofReadinessAction[]): ProofReadinessAction[] {
  return [...actions].sort((left, right) => {
    const rankDiff = proofActionDisplayRank(left) - proofActionDisplayRank(right);
    if (rankDiff !== 0) return rankDiff;
    return actionPriorityRank(left?.priority) - actionPriorityRank(right?.priority);
  });
}

const PRIMARY_BLOCKING_ACTION_ORDER = [
  'enable_clip_capture_required_types',
  'restore_high_risk_clips',
  'fix_degraded_inference',
  'investigate_degraded_pipeline',
  'review_open_zone_violations',
  'review_open_incidents',
  'review_hotspot',
  'reduce_evidence_delay',
  'fix_offline_hardware',
  'review_high_risk',
];

export function _pickPrimaryBlockingActionId(actions: ProofReadinessAction[]): string | undefined {
  for (const id of PRIMARY_BLOCKING_ACTION_ORDER) {
    if (actions.some((action) => String(action?.id || '') === id)) return id;
  }
  return undefined;
}

export function actionTextBlob(action?: ProofReadinessAction): string {
  return [action?.id, action?.label, action?.reason].map((value) => String(value || '').toLowerCase()).join(' ');
}

const EVIDENCE_CONFIDENCE_ACTION_IDS = new Set<string>([
  'enable_clip_capture_required_types',
  'restore_high_risk_clips',
  'fix_degraded_inference',
  'investigate_degraded_pipeline',
  'reduce_evidence_delay',
  'fix_offline_hardware',
  'regenerate_proof_readiness_inputs',
  'generate_proof_with_evidence_metrics',
  'confirm_reliability_metrics',
  'refresh_validation',
]);

export function isEvidenceConfidenceAction(action?: ProofReadinessAction): boolean {
  const id = String(action?.id || '');
  if (EVIDENCE_CONFIDENCE_ACTION_IDS.has(id)) return true;
  const text = actionTextBlob(action);
  return /(clip capture|missing clips|high-risk clips|degraded inference|pipeline diagnostics|evidence delay|evidence metrics|reliability metrics|refresh validation|offline camera|offline cameras|offline devices|camera\/device visibility)/.test(text);
}

const SAFETY_INTERVENTION_ACTION_IDS = new Set<string>([
  'review_open_zone_violations',
  'review_hotspot',
]);

export function isSafetyInterventionAction(action?: ProofReadinessAction): boolean {
  const id = String(action?.id || '');
  if (SAFETY_INTERVENTION_ACTION_IDS.has(id)) return true;
  const text = actionTextBlob(action);
  return /(zone controls|zone violations|danger-zone entries|open danger-zone|review hotspot|review high-risk|hotspot zone|top hotspot zone|high-risk area|layout \/ workflow)/i.test(text);
}

const OPERATOR_FOLLOWUP_ACTION_IDS = new Set<string>([
  'review_open_incidents',
  'review_high_risk',
  'review_warnings_before_approval',
]);

export function isOperatorFollowupAction(action?: ProofReadinessAction): boolean {
  const id = String(action?.id || '');
  if (OPERATOR_FOLLOWUP_ACTION_IDS.has(id)) return true;
  const text = actionTextBlob(action);
  return /(review open incidents|unresolved high-risk|critical risks open|outstanding warnings|open incidents remain|need operator follow-up)/i.test(text);
}

const EVIDENCE_ACTION_DISPLAY_ORDER = [
  'enable_clip_capture_required_types',
  'restore_high_risk_clips',
  'fix_degraded_inference',
  'investigate_degraded_pipeline',
  'reduce_evidence_delay',
  'regenerate_proof_readiness_inputs',
  'generate_proof_with_evidence_metrics',
  'confirm_reliability_metrics',
  'refresh_validation',
];

function evidenceActionDisplayRank(action?: ProofReadinessAction): number {
  const id = String(action?.id || '');
  const idx = EVIDENCE_ACTION_DISPLAY_ORDER.indexOf(id);
  if (idx >= 0) return idx;
  return 500;
}

export function sortEvidenceActionsForDisplay(actions: ProofReadinessAction[]): ProofReadinessAction[] {
  return [...actions].sort((left, right) => {
    const rankDiff = evidenceActionDisplayRank(left) - evidenceActionDisplayRank(right);
    if (rankDiff !== 0) return rankDiff;
    return actionPriorityRank(left?.priority) - actionPriorityRank(right?.priority);
  });
}

const SAFETY_ACTION_DISPLAY_ORDER = [
  'review_open_zone_violations',
  'review_hotspot',
];

function safetyActionDisplayRank(action?: ProofReadinessAction): number {
  const id = String(action?.id || '');
  const idx = SAFETY_ACTION_DISPLAY_ORDER.indexOf(id);
  if (idx >= 0) return idx;
  return 500;
}

export function sortSafetyActionsForDisplay(actions: ProofReadinessAction[]): ProofReadinessAction[] {
  return [...actions].sort((left, right) => {
    const rankDiff = safetyActionDisplayRank(left) - safetyActionDisplayRank(right);
    if (rankDiff !== 0) return rankDiff;
    return actionPriorityRank(left?.priority) - actionPriorityRank(right?.priority);
  });
}

const OPERATOR_ACTION_DISPLAY_ORDER = [
  'review_open_incidents',
  'review_high_risk',
  'review_warnings_before_approval',
];

function operatorActionDisplayRank(action?: ProofReadinessAction): number {
  const id = String(action?.id || '');
  const idx = OPERATOR_ACTION_DISPLAY_ORDER.indexOf(id);
  if (idx >= 0) return idx;
  return 500;
}

export function sortOperatorActionsForDisplay(actions: ProofReadinessAction[]): ProofReadinessAction[] {
  return [...actions].sort((left, right) => {
    const rankDiff = operatorActionDisplayRank(left) - operatorActionDisplayRank(right);
    if (rankDiff !== 0) return rankDiff;
    return actionPriorityRank(left?.priority) - actionPriorityRank(right?.priority);
  });
}

export function proofPackGeneratedAt(proofPackPreviewData: any): string | null {
  const candidate = [
    proofPackPreviewData?.manifest?.generated_at,
    proofPackPreviewData?.evidence_pack?.generated_at,
    proofPackPreviewData?.generated_at,
  ].find((value) => typeof value === 'string' && value);
  return candidate ? String(candidate) : null;
}

export function formatTimestamp(value?: string | null): string {
  const raw = String(value || '').trim();
  if (!raw) return '—';
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleString();
}

export function humanizeJobState(value?: string | null): string {
  const raw = String(value || '').trim();
  if (!raw) return 'Running';
  return raw
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

export function readEvidenceBundleReviewed(key: string): boolean {
  if (!key || typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

export function writeEvidenceBundleReviewed(key: string): void {
  if (!key || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, '1');
  } catch {
    // ignore localStorage errors
  }
}

export function parseInternalPath(raw?: string): string | null {
  const value = String(raw || '').trim();
  if (!value) return null;
  try {
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

export function buildConfigurationHandoffHref(args: {
  reportZoneId?: string | null;
  filterZoneId?: string | null;
  reportCameraIds?: string[] | null;
  filterDeviceId?: string | null;
  reportDeviceId?: string | null;
  filterCameraId?: string | null;
  reportPolicyType?: string | null;
  reportPolicyIds?: string[] | null;
}): string {
  const params = new URLSearchParams();
  const exactPolicyId = (Array.isArray(args.reportPolicyIds) ? args.reportPolicyIds : [])
    .map((value) => String(value || '').trim())
    .find(Boolean);
  const zoneId = String(args.reportZoneId || args.filterZoneId || '').trim();
  const deviceId = String(args.reportDeviceId || args.filterDeviceId || '').trim();
  const cameraId = (Array.isArray(args.reportCameraIds) ? args.reportCameraIds : [])
    .map((value) => String(value || '').trim())
    .find(Boolean) || String(args.filterCameraId || '').trim();
  const policyType = String(args.reportPolicyType || '').trim();

  if (exactPolicyId) params.set('policy_id', exactPolicyId);
  if (zoneId) params.set('zone_id', zoneId);
  if (deviceId) params.set('device_id', deviceId);
  if (cameraId) params.set('camera_id', cameraId);
  if (policyType) params.set('type', policyType);

  const query = params.toString();
  return `/policies${query ? `?${query}` : ''}`;
}

export function buildIncidentReviewReturnHref(args: {
  reportZoneId?: string | null;
  filterZoneId?: string | null;
  reportDeviceId?: string | null;
  filterDeviceId?: string | null;
  reportCameraIds?: string[] | null;
  filterCameraId?: string | null;
  reportPolicyType?: string | null;
  fromDate?: string | null;
  toDate?: string | null;
  reportFrom?: string | null;
  reportTo?: string | null;
  reportId?: string | null;
  safetyCaseId?: string | null;
  workflowId?: string | null;
  incidentId?: string | null;
  tableMode?: 'raw' | 'grouped' | null;
}): string {
  const params = new URLSearchParams();
  const zoneId = String(args.reportZoneId || args.filterZoneId || '').trim();
  const deviceId = String(args.reportDeviceId || args.filterDeviceId || '').trim();
  const cameraId = (Array.isArray(args.reportCameraIds) ? args.reportCameraIds : [])
    .map((value) => String(value || '').trim())
    .find(Boolean) || String(args.filterCameraId || '').trim();
  const incidentType = String(args.reportPolicyType || '').trim();
  const fromDate = String(args.fromDate || args.reportFrom || '').trim();
  const toDate = String(args.toDate || args.reportTo || '').trim();
  const reportFrom = String(args.reportFrom || args.fromDate || '').trim();
  const reportTo = String(args.reportTo || args.toDate || '').trim();
  const reportId = String(args.reportId || '').trim();
  const safetyCaseId = String(args.safetyCaseId || '').trim();
  const workflowId = String(args.workflowId || '').trim();
  const incidentId = String(args.incidentId || '').trim();
  const hasScanWindow = Boolean(fromDate || toDate || reportFrom || reportTo);
  const tableMode = String(args.tableMode || '').trim() === 'grouped'
    ? 'grouped'
    : (String(args.tableMode || '').trim() === 'raw' || hasScanWindow ? 'raw' : 'grouped');

  params.set('tab', 'open');
  params.set('table_mode', tableMode);
  params.set('focus', 'queue');
  params.set('review', '1');
  if (hasScanWindow) params.set('sort', 'oldest');
  if (zoneId) params.set('zone_id', zoneId);
  if (deviceId) params.set('device_id', deviceId);
  if (cameraId) params.set('camera_id', cameraId);
  if (incidentType) params.set('incident_type', incidentType);
  if (fromDate) params.set('from_date', fromDate);
  if (toDate) params.set('to_date', toDate);
  if (reportFrom) params.set('report_from', reportFrom);
  if (reportTo) params.set('report_to', reportTo);
  if (reportId) params.set('report_id', reportId);
  if (safetyCaseId) params.set('safety_case_id', safetyCaseId);
  if (workflowId) params.set('workflow_id', workflowId);
  if (incidentId) params.set('incident_id', incidentId);
  return buildScopedIncidentsPath(`/incidents?${params.toString()}`);
}

export function routeHrefMatchesSurface(href: string | null, targetSurface: RoutedCta['targetSurface']): boolean {
  if (!href) return false;
  if (targetSurface === 'incidents') return href.startsWith('/incidents');
  if (targetSurface === 'diagnostics') return href.startsWith('/diagnostics');
  if (targetSurface === 'device_control') return /^\/devices\/[^/]+\/control/.test(href);
  return false;
}

export function withPreferredActionHref(
  action: ProofReadinessAction,
  routed: RoutedActionResult,
): RoutedActionResult {
  const parsedHref = parseInternalPath(action?.href || '');
  const patchCta = (cta: RoutedCta | null): RoutedCta | null => {
    if (!cta || !parsedHref || !routeHrefMatchesSurface(parsedHref, cta.targetSurface)) return cta;
    return {
      ...cta,
      href: parsedHref,
      navigationTarget: parsedHref,
    };
  };
  return {
    ...routed,
    primaryCta: patchCta(routed.primaryCta),
    secondaryCtas: routed.secondaryCtas.map((cta) => patchCta(cta)).filter(Boolean) as RoutedCta[],
  };
}
